import type { SessionPermission } from '../lib/session/sessionContext';
import type { PermissionResolver } from '../ui/permissions/permissionContext';

/**
 * The adapter that turns the `/me` session into the FS-3 `PermissionResolver` the app already
 * publishes through `PermissionProvider`. It lives in `src/app` — not `src/lib` — precisely so the
 * session store never takes a dependency on `src/ui`'s permission-decision types; the composition
 * root legitimately imports both layers (F5-a Decision 3).
 *
 * The mapping is the whole of F5-a's authorization surface, and it is deliberately two-valued:
 *
 *   - a **held** key → `{ kind: 'allowed' }`
 *   - an **absent** key → `{ kind: 'denied-permanent' }`
 *
 * There is no `denied-contextual` because F5-a has no record to reason about — a contextual denial
 * ("wrong stage, not the owner") needs ownership context this feature does not carry (spec § Out of
 * Scope). A later feature that needs one wraps or refines this resolver. The `resource` argument is
 * ignored for the same reason: membership is the same everywhere. Absence reading as
 * `denied-permanent` is exactly what design-system §9's hide-vs-disable rule expects (BE README
 * § "the frontend seam").
 */
export function sessionPermissionResolver(permissions: readonly SessionPermission[]): PermissionResolver {
    const held = new Set(permissions.map((permission) => permission.key));
    return (permission) => (held.has(permission) ? { kind: 'allowed' } : { kind: 'denied-permanent' });
}
